<template>
  <div class="reservation-list">
    <main class="main-content">
      <h1 class="title-with-underline">預約資訊</h1>

      <!-- 預約列表 -->
      <div class="appointment-list" v-if="reservations.length > 0 && !isLoadingReservations">
        <ReservationCard
          v-for="(reservation, index) in reservations"
          :key="reservation.reservationId"
          :reservation="reservation"
          :isExpanded="expandedCards[index]"
          :isSelected="selectedReservation && selectedReservation.reservationId === reservation.reservationId"
          @click="toggleCard(reservation, index)"
        />
      </div>

      <!-- 空狀態 -->
      <div class="empty-state" v-if="reservations.length === 0 && !isLoading">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
        </div>
        <p class="empty-text">您目前沒有預約紀錄</p>
      </div>

      <!-- 載入更多 -->
      <button
        v-if="hasMore && !isLoadingReservations"
        class="btn btn-secondary btn-block"
        @click="loadMore"
        :disabled="isLoadingMore"
      >
        {{ isLoadingMore ? '載入中...' : '載入更多' }}
      </button>
    </main>

    <!-- 底部按鈕 -->
    <div style="padding-bottom: 96px"></div>
    <div class="fixed-footer-navigation">
      <div class="button-group">
        <button class="btn btn-secondary" @click="goBack">返回</button>
        <button class="btn btn-danger" :disabled="!selectedReservation" @click="showCancelModal">取消預約</button>
      </div>
    </div>

    <!-- 取消預約確認 Modal -->
    <div class="tongxin-modal" :class="{ show: showCancelConfirmModal }">
      <div class="tongxin-modal__content">
        <div class="tongxin-modal__header">
          <div class="tongxin-modal__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h2 class="tongxin-modal__title">確認取消預約</h2>
        </div>
        <div class="tongxin-modal__body">
          <p class="tongxin-modal__text">
            確定要取消以下預約嗎？<br>
            取消後將無法復原，需重新預約。
          </p>
          <div class="cancel-info-box" v-if="selectedReservation">
            <div class="cancel-info-item">
              <span class="cancel-info-label">看診人</span>
              <span class="cancel-info-value text-red">{{ selectedReservation.summary.patientName }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">診所名稱</span>
              <span class="cancel-info-value">{{ selectedReservation.summary.clinicName }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">看診日期</span>
              <span class="cancel-info-value text-red">{{ formattedDate(selectedReservation.summary.visitDate) }}</span>
            </div>
            <div class="cancel-info-item">
              <span class="cancel-info-label">掛號類型</span>
              <span class="cancel-info-value">{{ selectedReservation.summary.typeName }}</span>
            </div>
          </div>
        </div>
        <div class="tongxin-modal__buttons">
          <button class="btn btn-secondary" @click="closeModal">返回</button>
          <button class="btn btn-danger" @click="confirmCancel">確認取消</button>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <b-spinner style="border-width: 2px" />
    </div>
  </div>
</template>

<script>
import tongxinApi from '@/apis/web/tongxin'
import stepController from '@/mixins/web/tongxin/stepController'
import ReservationCard from '@/components/web/proj/tongxin/ReservationCard.vue'

export default {
  name: 'ReservationList',
  components: {
    ReservationCard
  },
  inject: ['clinicIdData', 'showSuccessModal', 'showErrorModal'],
  data() {
    return {
      reservations: [],
      isLoadingCustomer: false,
      isLoadingReservations: false,
      isLoadingMore: false,
      isCancellingReservation: false,
      offset: 0,
      limit: 20,
      hasMore: false,
      ssid: '',
      birthday: '',
      expandedCards: {},
      selectedReservation: null,
      showCancelConfirmModal: false,
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingCustomer || this.isLoadingReservations || this.isLoadingMore || this.isCancellingReservation
    },
    clinicId() {
      return this.clinicIdData?.value || null
    }
  },
  async created() {
    await this.loadCustomerInfo()
    await this.fetchReservations()
  },
  methods: {
    /**
     * 取得本人身分資料（ssid/birthday），供查詢預約 API 使用
     */
    async loadCustomerInfo() {
      this.isLoadingCustomer = true
      try {
        const { data: res = {} } = await tongxinApi.getCustomerInfo()
        this.ssid = res.customer_code || ''
        this.birthday = res.birthday || ''
      } catch (error) {
        console.error('取得本人資料失敗:', error)
      } finally {
        this.isLoadingCustomer = false
      }
    },

    /**
     * 取得預約列表
     */
    async fetchReservations() {
      this.isLoadingReservations = true

      try {
        const response = await tongxinApi.getReservations(
          this.ssid,
          this.birthday,
          'all',
          null,
          null,
          this.limit,
          this.offset
        )

        const data = response.data || response || []
        this.reservations = Array.isArray(data) ? data : []
        this.hasMore = this.reservations.length >= this.limit
      } catch (error) {
        console.error('取得預約列表失敗:', error)
        this.reservations = []
      } finally {
        this.isLoadingReservations = false
      }
    },

    /**
     * 載入更多
     */
    async loadMore() {
      if (this.isLoadingMore) return

      this.isLoadingMore = true
      this.offset += this.limit

      try {
        const response = await tongxinApi.getReservations(
          this.ssid,
          this.birthday,
          'all',
          null,
          null,
          this.limit,
          this.offset
        )

        const data = response.data || response || []
        const newReservations = Array.isArray(data) ? data : []

        this.reservations = [...this.reservations, ...newReservations]
        this.hasMore = newReservations.length >= this.limit
      } catch (error) {
        console.error('載入更多預約失敗:', error)
        // 回退 offset
        this.offset -= this.limit
      } finally {
        this.isLoadingMore = false
      }
    },

    /**
     * 切換卡片展開狀態
     */
    toggleCard(reservation, index) {
      // 切換展開狀態
      this.$set(this.expandedCards, index, !this.expandedCards[index])

      // 選中卡片
      this.selectCard(reservation)
    },

    /**
     * 選中卡片
     */
    selectCard(reservation) {
      this.selectedReservation = reservation
    },

    /**
     * 格式化日期（用於 Modal）
     */
    formattedDate(visitDate) {
      const date = new Date(visitDate)
      if (isNaN(date.getTime())) return visitDate

      const month = date.getMonth() + 1
      const day = date.getDate()
      const weekday = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]

      return `${month}/${day} (${weekday})`
    },

    /**
     * 顯示取消預約 Modal
     */
    showCancelModal() {
      if (!this.selectedReservation) {
        alert('請先選擇要取消的預約')
        return
      }
      this.showCancelConfirmModal = true
    },

    /**
     * 關閉取消確認 Modal
     */
    closeModal() {
      this.showCancelConfirmModal = false
    },

    /**
     * 確認取消預約
     */
    async confirmCancel() {
      this.closeModal()

      try {
        this.isCancellingReservation = true
        await tongxinApi.cancelReservation(this.selectedReservation.reservationId)

        this.showSuccessModal(
          '預約已成功取消',
          '您的預約已成功取消，如需重新預約請至預約頁面操作。'
        )

        this.reservations = this.reservations.filter(
          r => r.reservationId !== this.selectedReservation.reservationId
        )
        this.selectedReservation = null
      } catch (error) {
        console.error('取消預約失敗:', error)
        this.showErrorModal(
          '預約取消失敗',
          error.message || '系統忙碌中，請稍後再試。若問題持續發生，請聯繫客服人員。'
        )
      } finally {
        this.isCancellingReservation = false
      }
    },

    /**
     * 返回上一頁
     */
    goBack() {
      stepController.methods.goToLineOfficialAccount.call(this)
    }
  }
}
</script>

<style lang="scss" scoped>
.reservation-list {
  position: relative;
  min-height: 100vh;
}

.main-content {
  padding: 24px 16px 0;
  min-height: calc(100vh - 200px);
  max-width: 800px;
  margin: 0 auto;
  overflow-x: hidden;
}

/* 預約卡片列表 */
.appointment-list {
  display: flex;
  flex-direction: column;
}

/* Modal */
.tongxin-modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 2000;
  justify-content: center;
  align-items: center;

  &.show {
    display: flex;
  }

  &__content {
    background: #fff;
    border-radius: 16px;
    padding: 32px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    animation: modalSlideIn 0.3s ease-out;
  }

  &__header {
    text-align: center;
    margin-bottom: 24px;
  }

  &__icon {
    width: 60px;
    height: 60px;
    background: #fee2e2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  &__icon svg {
    width: 30px;
    height: 30px;
    color: #dc2626;
  }

  &__title {
    font-size: 26px;
    font-weight: bold;
    color: #333;
    margin-bottom: 8px;
  }

  &__body {
    margin-bottom: 24px;
  }

  &__text {
    font-size: 20px;
    color: #666;
    line-height: 1.6;
    text-align: center;
    margin-bottom: 20px;
  }

  &__buttons {
    display: flex;
    justify-content: center;
    gap: 12px;

    .btn + .btn {
      margin-left: auto;
    }
  }
}

.cancel-info-box {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  border-left: 4px solid #dc2626;
}

.cancel-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.cancel-info-item:last-child {
  border-bottom: none;
}

.cancel-info-label {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.cancel-info-value {
  font-size: 19px;
  color: #333;
  font-weight: 600;
  text-align: right;
}

/* 空狀態 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon svg {
  width: 50px;
  height: 50px;
  color: #ccc;
}

.empty-text {
  font-size: 20px;
  color: #999;
  margin-bottom: 24px;
}

/* 底部按鈕 */
.fixed-footer-navigation {
  .button-group {
    display: flex;
    justify-content: center;
    gap: 20px;
    max-width: 800px;
    margin: 0 auto;

    .btn {
      flex: 1;
    }

    .btn + .btn {
      margin-left: auto;
    }
  }
}

/* 按鈕樣式 */
.btn-block {
  width: 100%;
  margin-bottom: 12px;
}

@media (max-width: 767px) {
  .main-content {
    padding: 20px;
  }

  .tongxin-modal__content {
    padding: 24px;
  }

  .tongxin-modal__title {
    font-size: 22px;
  }

  .cancel-info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 10px 0;
  }

  .cancel-info-label {
    font-size: 15px;
  }

  .cancel-info-value {
    font-size: 19px;
    text-align: left;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
